<template>
  <StaticPageLayout
    title="Çerez Politikası - INSATEQ"
    description="INSATEQ çerez politikası. Web sitemizde kullanılan çerezler ve bunların nasıl yönetildiği hakkında bilgi."
  >
    <!-- Hero -->
    <div class="bg-gradient-to-br from-blue-600 to-indigo-700 py-16">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 class="text-3xl md:text-4xl font-bold text-white mb-4">
          Çerez Politikası
        </h1>
        <p class="text-blue-100 text-lg">
          Son güncelleme: {{ new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' }) }}
        </p>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div class="prose prose-lg max-w-none">
        <!-- Section 1 -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">1</span>
            Çerez Nedir?
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            Çerezler (cookies), web sitelerinin tarayıcınıza yerleştirdiği küçük metin dosyalarıdır.
            Bu dosyalar, siteyi tekrar ziyaret ettiğinizde sizi tanımak, tercihlerinizi hatırlamak
            ve size daha iyi bir kullanıcı deneyimi sunmak için kullanılır.
          </p>
          <p class="text-gray-600 leading-relaxed">
            INSATEQ olarak, hizmet kalitemizi artırmak ve platformumuzu geliştirmek amacıyla çerezler kullanmaktayız.
          </p>
        </section>

        <!-- Section 2 -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">2</span>
            Kullandığımız Çerez Türleri
          </h2>

          <!-- Essential Cookies -->
          <div class="bg-gray-50 rounded-xl p-6 mb-4">
            <h3 class="font-semibold text-gray-900 mb-2 flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              Zorunlu Çerezler
            </h3>
            <p class="text-gray-600 text-sm mb-2">
              Bu çerezler, platformumuzun temel işlevlerinin çalışması için gereklidir ve devre dışı bırakılamaz.
            </p>
            <ul class="list-disc pl-6 text-gray-600 text-sm space-y-1">
              <li><strong>Oturum Çerezi:</strong> Giriş yaptıktan sonra oturumunuzu aktif tutar</li>
              <li><strong>CSRF Koruma:</strong> Güvenlik amacıyla form işlemlerinde kullanılır</li>
              <li><strong>Çerez Tercihi:</strong> Çerez tercihlerinizi hatırlar</li>
            </ul>
          </div>

          <!-- Functional Cookies -->
          <div class="bg-gray-50 rounded-xl p-6 mb-4">
            <h3 class="font-semibold text-gray-900 mb-2 flex items-center">
              <svg class="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" />
              </svg>
              İşlevsel Çerezler
            </h3>
            <p class="text-gray-600 text-sm mb-2">
              Bu çerezler, platformu daha iyi kullanmanızı sağlar.
            </p>
            <ul class="list-disc pl-6 text-gray-600 text-sm space-y-1">
              <li><strong>Dil Tercihi:</strong> Seçtiğiniz dili hatırlar</li>
              <li><strong>Tema Tercihi:</strong> Açık/koyu tema tercihinizi saklar</li>
              <li><strong>Sayfa Ayarları:</strong> Tablo görünümü ve filtre tercihlerinizi hatırlar</li>
            </ul>
          </div>

          <!-- Analytics Cookies -->
          <div class="bg-gray-50 rounded-xl p-6">
            <h3 class="font-semibold text-gray-900 mb-2 flex items-center">
              <svg class="w-5 h-5 text-purple-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
              Analitik Çerezler
            </h3>
            <p class="text-gray-600 text-sm mb-2">
              Bu çerezler, platformumuzu nasıl kullandığınızı anlamamıza yardımcı olur.
            </p>
            <ul class="list-disc pl-6 text-gray-600 text-sm space-y-1">
              <li><strong>Sayfa Görüntülemesi:</strong> Hangi sayfaların ziyaret edildiğini izler</li>
              <li><strong>Performans Metrikleri:</strong> Sayfa yükleme sürelerini ölçer</li>
              <li><strong>Hata Takibi:</strong> Oluşabilecek hataları tespit eder</li>
            </ul>
          </div>
        </section>

        <!-- Section 3 -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">3</span>
            Çerez Saklama Süreleri
          </h2>
          <div class="overflow-x-auto">
            <table class="min-w-full border-collapse">
              <thead>
                <tr class="bg-gray-50">
                  <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Çerez Türü</th>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Saklama Süresi</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr>
                  <td class="px-4 py-3 text-sm text-gray-600">Oturum Çerezi</td>
                  <td class="px-4 py-3 text-sm text-gray-600">Tarayıcı kapatılınca silinir</td>
                </tr>
                <tr>
                  <td class="px-4 py-3 text-sm text-gray-600">Kimlik Doğrulama</td>
                  <td class="px-4 py-3 text-sm text-gray-600">8 saat (veya oturum kapatılınca)</td>
                </tr>
                <tr>
                  <td class="px-4 py-3 text-sm text-gray-600">Tercihler</td>
                  <td class="px-4 py-3 text-sm text-gray-600">1 yıl</td>
                </tr>
                <tr>
                  <td class="px-4 py-3 text-sm text-gray-600">Çerez Onayı</td>
                  <td class="px-4 py-3 text-sm text-gray-600">1 yıl</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Section 4 -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">4</span>
            Çerezleri Yönetme
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            Çerez tercihlerinizi istediğiniz zaman değiştirebilirsiniz. Tarayıcınızın ayarlarından çerezleri yönetebilir,
            silebilir veya engelleyebilirsiniz.
          </p>
          <div class="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <div class="flex items-start">
              <svg class="w-6 h-6 text-yellow-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p class="font-medium text-yellow-800">Önemli Bilgi</p>
                <p class="text-yellow-700 text-sm mt-1">
                  Zorunlu çerezleri devre dışı bırakmanız durumunda platformumuzun bazı özelliklerinin düzgün çalışmayabileceğini lütfen unutmayınız.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- Section 5 -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">5</span>
            Tarayıcı Ayarları
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            Popüler tarayıcılarda çerez ayarlarına aşağıdaki yollarla ulaşabilirsiniz:
          </p>
          <div class="grid md:grid-cols-2 gap-4">
            <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" class="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
              <p class="text-gray-700 text-sm font-medium">Google Chrome</p>
              <p class="text-gray-500 text-xs">Ayarlar > Gizlilik ve güvenlik > Çerezler</p>
            </a>
            <a href="https://support.mozilla.org/tr/kb/cerezleri-etkinlestirme-ve-devre-disi-birakma" target="_blank" rel="noopener noreferrer" class="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
              <p class="text-gray-700 text-sm font-medium">Mozilla Firefox</p>
              <p class="text-gray-500 text-xs">Ayarlar > Gizlilik ve Güvenlik > Çerezler</p>
            </a>
            <a href="https://support.apple.com/tr-tr/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" class="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
              <p class="text-gray-700 text-sm font-medium">Safari</p>
              <p class="text-gray-500 text-xs">Tercihler > Gizlilik > Çerezler</p>
            </a>
            <a href="https://support.microsoft.com/tr-tr/microsoft-edge/cerezleri-silme-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" class="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
              <p class="text-gray-700 text-sm font-medium">Microsoft Edge</p>
              <p class="text-gray-500 text-xs">Ayarlar > Çerezler ve site izinleri</p>
            </a>
          </div>
        </section>

        <!-- Section 6 - Contact -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">6</span>
            İletişim
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            Çerezlerle ilgili sorularınız için bizimle iletişime geçebilirsiniz:
          </p>
          <div class="bg-blue-50 rounded-xl p-6">
            <div class="flex items-center mb-3">
              <svg class="w-5 h-5 text-blue-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <a href="mailto:destek@insateq.com" class="text-blue-600 hover:text-blue-700 font-medium">destek@insateq.com</a>
            </div>
          </div>
        </section>
      </div>
    </div>
  </StaticPageLayout>
</template>

<script setup>
import StaticPageLayout from '@/Layouts/StaticPageLayout.vue'
</script>
